import React from "react";
import TemporaryMessage from "./TemporaryMessage";
import { ApplicationPaths, LogoutActions, QueryParameterNames } from "@services/authorizationService/ApiAuthorizationConstants";
import { serviceManager } from "@services/workers";
import { AuthenticationResultStatus, authService } from "@services/authorizationService";
import { AuthContext } from "@utils/authProvider";

// The main responsibility of this component is to handle the user's logout process.
// This is the starting point for the logout process, which is usually initiated when a
// user clicks on the logout button on the LoginMenu component.
interface LogoutProps {
  returnUrl: string | undefined;
  action: string;
}

const processAction = async (action: string,
  isAuthenticated: boolean,
  setMessage: (value: React.SetStateAction<string | undefined>) => void,
  setCounter: (value: React.SetStateAction<number>) => void,
  setIsReady: (value: React.SetStateAction<boolean>) => void) => {
  switch (action) {
    case LogoutActions.Logout:
      if (!!window.history.state) {
        await logout(isAuthenticated, getReturnUrl(), setMessage);
      } else {
        // This prevents regular links to <app>/authentication/logout from triggering a logout
        setIsReady(true);
        setMessage("The logout was not initiated from within the page.");
      }
      break;
    case LogoutActions.LogoutCallback:
      await processLogoutCallback(setMessage);
      break;
    case LogoutActions.LoggedOut:
      // state.LoggedUser = undefined;
      // state.Role = undefined;
      authService.notifySubscribers();
      serviceManager.stopAllServices();

      setIsReady(true);
      setMessage("You successfully logged out!");
      setCounter((prevCounter: number) => prevCounter - 1);

      const interval = setInterval(() => {
        setIsReady(true);
        setMessage("Loading...");
        setCounter((prevCounter: number) => {
          if (prevCounter === 0) {
            clearInterval(interval);
            return 0;
          }
          return prevCounter - 1;
        });
      }, 1000);

      setTimeout(() => navigateToReturnUrl("/"), 1000);
      break;

    default:
      throw new Error(`Invalid action '${action}'`);
  }

  setIsReady(true);
}

const getMessage = (action: string, isReady: boolean, message: string | undefined) => {
  if (!isReady) {
    return <TemporaryMessage message={"Loading..."} />;
  }
  if (!!message) {
    return <TemporaryMessage message={message} />;
  } else {
    switch (action) {
      case LogoutActions.Logout:
        return <TemporaryMessage message="Please wait while you are being logged out..." />;
      case LogoutActions.LogoutCallback:
        return <TemporaryMessage message="Please wait while you are being logged out..." />;
      case LogoutActions.LoggedOut:
        return <TemporaryMessage message={message || ""} />;
      default:
        return <TemporaryMessage message={"Loading..."} />;
    }
  }
}

const Logout: React.FC<LogoutProps> = ({ action }) => {
  const { isAuthenticated } = React.useContext(AuthContext);
  const [message, setMessage] = React.useState<string | undefined>(undefined);
  const [isReady, setIsReady] = React.useState<boolean>(false);
  const [, setCounter] = React.useState<number>(3);

  React.useEffect(() => {
    processAction(action, isAuthenticated, setMessage, setCounter, setIsReady);
  }, [action, isAuthenticated]);

  return (getMessage(action, isReady, message));
}

const logout = async (isAuthenticated: boolean, returnUrl: string, setMessage: { (value: React.SetStateAction<string | undefined>): void; (arg0: string): void; (arg0: string): void; }) => {
  const state = { returnUrl };

  if (isAuthenticated) {
    // await authService.enableAutoBooking();

    const result = await authService.signOut(state);
    switch (result.status) {
      case AuthenticationResultStatus.Redirect:
        break;
      case AuthenticationResultStatus.Success:
        navigateToReturnUrl(returnUrl);
        break;
      case AuthenticationResultStatus.Fail:
        setMessage(result.status);
        break;
      default:
        throw new Error("Invalid authentication result status.");
    }
  } else {
    setMessage("You successfully logged out!");
  }
}

const processLogoutCallback = async (setMessage: { (value: React.SetStateAction<string | undefined>): void; (arg0: string): void; (arg0: string): void; }) => {
  const url = window.location.href;
  const result = await authService.completeSignOut(url);
  switch (result.status) {
    case AuthenticationResultStatus.Redirect:
      // There should not be any redirects as the only time completeAuthentication finishes
      // is when we are doing a redirect sign in flow.
      throw new Error("Should not redirect.");
    case AuthenticationResultStatus.Success:
      navigateToReturnUrl(getReturnUrl(result.state));
      break;
    case AuthenticationResultStatus.Fail:
      setMessage(result.status);
      break;
    default:
      throw new Error("Invalid authentication result status.");
  }
}

const getReturnUrl = (state?: { returnUrl: string; }) => {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get(QueryParameterNames.ReturnUrl);

  if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
    // This is an extra check to prevent open redirects.
    throw new Error("Invalid return url. The return url needs to have the same origin as the current page.");
  }
  return (state && state.returnUrl) || fromQuery || `${window.location.origin}${ApplicationPaths.LoggedOut}`;
}

const navigateToReturnUrl = (returnUrl: string) => {
  return window.location.replace(returnUrl);
}

export default React.memo(Logout);